<template>
  <div>
    <v-card id="clinic_prefs_edit_modal" data-testid="clinic_prefs_edit">
    <v-card-title style="padding: 0px">
      <v-toolbar grey flat>
        <v-btn icon @click="close_prefs_edit" data-testid="clinic_prefs_edit_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('clinic_prefs_edit.title') }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>
    <v-card-text>
      <v-container fluid class="pa-0">
        <v-layout>
          <!-- Left Navigation Menu -->
          <v-flex xs3 class="pr-4">
            <v-card outlined>
              <v-list dense>
                <v-list-item
                  v-for="(section, index) in sections"
                  :key="index"
                  @click="selectedSection = section.id"
                  :class="{ 'v-list-item--active': selectedSection === section.id }"
                  class="mb-1"
                >
                  <v-list-item-icon>
                    <v-icon>{{ section.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ section.title }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-flex>

          <!-- Content Area -->
          <v-flex xs9>
            <!-- Default User Permissions Section -->
            <div v-if="selectedSection === 'permissions'">
              <v-card outlined class="pa-4">
                <v-card-title class="px-0 py-2">
                  <h4>{{ $t('clinic_prefs_edit.default_patient_permissions') }}</h4>
                </v-card-title>
                <v-card-text class="px-0 py-2">
                  <p class="text-caption text--secondary mb-2">{{ $t('clinic_prefs_edit.default_patient_permissions_help') }}</p>
                  <v-layout>
                    <v-flex xs4>
                      <v-checkbox
                        v-model="local_prefs.default_user_perms.view_tools"
                        :label="$t('clinic_prefs_edit.view_tools')"
                        dense
                      />
                      <v-checkbox
                        v-model="local_prefs.default_user_perms.view_printables"
                        :label="$t('clinic_prefs_edit.view_printables')"
                        dense
                      />
                      <v-checkbox
                        v-model="local_prefs.default_user_perms.view_progress_report"
                        :label="$t('clinic_prefs_edit.view_progress_report')"
                        dense
                      />
                    </v-flex>
                    <v-flex xs8 class="pl-4">
                      <p class="text-caption text--secondary mb-2">{{ $t('clinic_prefs_edit.view_tools_help') }}</p>
                      <p class="text-caption text--secondary mb-2">{{ $t('clinic_prefs_edit.view_printables_help') }}</p>
                      <p class="text-caption text--secondary mb-2">{{ $t('clinic_prefs_edit.view_progress_report_help') }}</p>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </div>

            <!-- Anaglyph Glasses Section -->
            <div v-if="selectedSection === 'anaglyph_glasses'">
              <v-card outlined class="pa-4">
                <v-card-title class="px-0 py-2">
                  <h4>{{ $t('clinic_prefs_edit.anaglyph_glasses') }}</h4>
                </v-card-title>
                <v-card-text class="px-0 py-2">
                  <p class="text-caption text--secondary mb-4">
                    {{ $t('clinic_prefs_edit.anaglyph_glasses_help') }}
                  </p>
                  <v-select
                    v-model="local_prefs.default_anaglyph_orientation"
                    :items="anaglyph_orientation_options"
                    :label="$t('clinic_prefs_edit.default_anaglyph_orientation')"
                    data-testid="clinic_prefs_edit_default_anaglyph_orientation"
                  />
                  <v-switch
                    v-model="local_prefs.disallow_different_glasses"
                    :label="$t('clinic_prefs_edit.disallow_different_glasses')"
                    data-testid="clinic_prefs_edit_disallow_different_glasses"
                  />
                  <p class="text-caption text--secondary">
                    {{ $t('clinic_prefs_edit.disallow_different_glasses_help') }}
                  </p>
                </v-card-text>
              </v-card>
            </div>

            <!-- Whitelabel Settings Section -->
            <div v-if="selectedSection === 'whitelabel'">
              <h2 class="mb-2">{{ $t('clinic_prefs_edit.whitelabel_settings') }}</h2>
              <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.whitelabel_description') }}</p>
              
              <!-- Colour Settings Section -->
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="px-0 py-2">
                  <h4>{{ $t('clinic_prefs_edit.colour_settings') }}</h4>
                </v-card-title>
                <v-card-text class="px-0 py-2">
                  <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.colour_settings_help') }}</p>
                  
                  <!-- Colour Pickers -->
                  <v-layout justify-space-between>
                    <v-flex xs3>
                      <!-- Primary Colour Picker -->
                      <v-menu
                        v-model="primary_color_menu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="local_prefs.whitelabel.primary_colour"
                            :label="$t('clinic_prefs_edit.primary_colour')"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            prepend-icon="mdi-palette"
                          >
                            <template v-slot:append>
                              <v-avatar
                                v-if="local_prefs.whitelabel.primary_colour"
                                :style="{ backgroundColor: local_prefs.whitelabel.primary_colour }"
                                size="24"
                                class="ml-2"
                              />
                            </template>
                          </v-text-field>
                        </template>
                        <v-card max-width="300">
                          <v-color-picker
                            v-model="local_prefs.whitelabel.primary_colour"
                            flat
                          />
                          <v-card-actions>
                            <v-spacer />
                            <v-btn
                              color="primary"
                              small
                              @click="primary_color_menu = false"
                            >
                              {{ $t('common.ok') }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </v-flex>

                    <v-flex xs3>
                      <!-- Dark Mode Toggle -->
                      <v-switch
                        v-model="local_prefs.whitelabel.dark_mode"
                        :label="$t('clinic_prefs_edit.dark_mode')"
                        color="primary"
                        hide-details
                        class="mt-4"
                      />
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>

              <!-- Logo Settings Section -->
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="px-0 py-2">
                  <h4>{{ $t('clinic_prefs_edit.logo_settings') }}</h4>
                </v-card-title>
                <v-card-text class="px-0 py-2">
                  <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.logo_settings_help') }}</p>
                  
                  <!-- Logo Upload with Inline Preview -->
                  <v-layout align-center>
                    <v-flex xs8>
                      <v-file-input
                        v-model="logo_file"
                        prepend-icon="mdi-image"
                        :label="$t('clinic_prefs_edit.logo')"
                        accept=".png,.jpg,.jpeg,.webp,.avif"
                        @change="on_logo_selected"
                      />
                    </v-flex>
                    <v-flex xs4 class="pl-4">
                      <div
                        v-if="logo_image_url"
                        class="logo-preview-container"
                        @mouseenter="show_remove_button = true"
                        @mouseleave="show_remove_button = false"
                      >
                        <v-img
                          :src="logo_image_url"
                          max-width="200"
                          contain
                          class="ml-2"
                        />
                        <div
                          v-if="show_remove_button"
                          class="remove-logo-overlay"
                        >
                          <v-btn
                            color="error"
                            small
                            @click="remove_logo"
                            class="remove-logo-btn"
                          >
                            {{ $t('clinic_prefs_edit.remove_logo') }}
                          </v-btn>
                        </div>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </div>

            <!-- Custom Categories Section -->
            <div v-if="selectedSection === 'categories'">
              <ClinicPrefsEditCategories
                v-model="local_prefs.custom_categories"
                :clinic_id="clinic.id"
              />
            </div>

            <!-- Help Contact Section -->
            <div v-if="selectedSection === 'help_text'">
              <v-card outlined class="pa-4">
                <v-card-title class="px-0 py-2">
                  <h4>{{ $t('clinic_prefs_edit.help_text') }}</h4>
                </v-card-title>
                <v-card-text class="px-0 py-2">
                  <p class="text-caption text--secondary mb-4" style="white-space: pre-line;">{{ $t('clinic_prefs_edit.help_text_description') }}</p>
                  <QuillEditor
                    v-model="local_prefs.custom_help_text"
                    :placeholder="$t('clinic_prefs_edit.custom_help_text')"
                    style="height: 200px; margin-bottom: 20px"
                  />
                </v-card-text>
              </v-card>
            </div> 

            <!-- Notifications Section -->
            <div v-if="selectedSection === 'notifications'">
              <ClinicPrefsEditNotifications
                v-model="local_prefs"
                :clinic_id="clinic.id"
              />
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-alert
        v-if="error_message"
        outlined
        type="error"
        :value="error_message"
      >
        {{ error_message }}
      </v-alert>
      <v-spacer />
      <v-btn color="primary" @click="submit" data-testid="clinic_prefs_edit_save">
        {{ $t('common.save_changes') }}
      </v-btn>
    </v-card-actions>
    <v-progress-linear v-if="in_flight" :indeterminate="true" />
  </v-card>


  </div>
</template>

<script>
import axios from 'axios';
import Vue from 'vue';
import ClinicPrefs from '../classes/clinic_prefs';
import { mapState } from 'vuex';
import ClinicPrefsEditCategories from './ClinicPrefsEditCategories.vue';
import ClinicPrefsEditNotifications from './ClinicPrefsEditNotifications.vue';
import QuillEditor from './QuillEditor.vue';

export default {
  components: {
    ClinicPrefsEditCategories,
    ClinicPrefsEditNotifications,
    QuillEditor,
  },
  props: {
    clinic: {
      type: Object,
      required: true,
    },
  },
  data() {
    let default_view_tools = true;
    let default_view_printables = true;
    let default_view_progress_report = false;
    if (this.clinic.has_tag("default_allow_view_tools")) {
      default_view_tools = this.clinic.tag_value("default_allow_view_tools");
    }
    if (this.clinic.has_tag("default_allow_view_printables")) {
      default_view_printables = this.clinic.tag_value("default_allow_view_printables");
    }
    if (this.clinic.has_tag("default_allow_progress_report")) {
      default_view_progress_report = this.clinic.tag_value("default_allow_progress_report");
    }

    return {
      local_prefs: {
        clinic_id: this.clinic.id,
        default_user_perms: {
          view_tools: default_view_tools,
          view_printables: default_view_printables,
          view_progress_report: default_view_progress_report,
        },
        user_signup_token: '',
        whitelabel: {
          primary_colour: null,
          secondary_colour: null,
          dark_mode: false,
          logo: null,
          email_template_invitation: null,
          email_template_password_recovery: null,
        },
        custom_categories: [],
        notification_email: '',
        notifications: {
          user_signup_from_token: false,
        },
        custom_help_text: null,
        default_anaglyph_orientation:
          (window.app_config && window.app_config.default_anaglyph_orientation) || 'cyan-red',
        disallow_different_glasses: false,
      },
      in_flight: false,
      error_message: '',
      logo_file: null,
      existing_logo_url: null,
      logo_preview_url: null,
      show_remove_button: false,
      primary_color_menu: false,
      secondary_color_menu: false,
      selectedSection: 'permissions', // Default selected section
    };
  },
  computed: {
    ...mapState(['current_clinic_id', 'app_config']),
    anaglyph_orientation_options() {
      return [
        { value: 'red-cyan', text: this.$t('calibration.red_left_blue_right') },
        { value: 'cyan-red', text: this.$t('calibration.blue_left_red_right') },
        { value: 'red-green', text: this.$t('calibration.red_left_green_right') },
        { value: 'green-red', text: this.$t('calibration.green_left_red_right') },
      ];
    },
    logo_image_url() {
      return this.logo_preview_url || this.existing_logo_url;
    },
    current_theme() {
      return this.$vuetify.theme.isDark ? 'dark' : 'light'
    },
    theme_primary_color() {
      return this.$vuetify.theme.themes[this.current_theme].primary
    },
    theme_secondary_colour() {
      return this.$vuetify.theme.themes[this.current_theme].secondary
    },
    show_notifications_section() {
      return this.clinic.has_tag("user_signup_token");
    },
    sections() {
      let sections = [
        { id: 'permissions', title: this.$t('clinic_prefs_edit.permissions'), icon: 'mdi-account-group' },
        { id: 'anaglyph_glasses', title: this.$t('clinic_prefs_edit.anaglyph_glasses'), icon: 'mdi-glasses' },
        { id: 'whitelabel', title: this.$t('clinic_prefs_edit.whitelabel'), icon: 'mdi-palette' },
        { id: 'categories', title: this.$t('clinic_prefs_edit.custom_categories'), icon: 'mdi-folder-multiple' },
        { id: 'help_text', title: this.$t('clinic_prefs_edit.help_text'), icon: 'mdi-help-circle' },
      ];
      if (this.show_notifications_section) {
        sections.push({ id: 'notifications', title: this.$t('clinic_prefs_edit.notifications'), icon: 'mdi-bell-ring' });
      }
      return sections;
    },
  },
  mounted() {
    this.load_prefs();
  },
  methods: {
    load_prefs() {
      axios.get('/api/clinic/' + this.clinic.id + '/prefs').then((response) => {
        let resp = response.data;
        if (resp.status == 'success') {
          this.local_prefs = resp.data;
          if (this.local_prefs.whitelabel && this.local_prefs.whitelabel.logo) {
            this.fetch_logo_url(this.local_prefs.whitelabel.logo, false);
          }
          if (!this.local_prefs.whitelabel.primary_colour) {
            Vue.set(this.local_prefs.whitelabel, 'primary_colour', this.theme_primary_color);
          }
          if (!this.local_prefs.whitelabel.secondary_colour) {
            Vue.set(this.local_prefs.whitelabel, 'secondary_colour', this.theme_secondary_colour);
          }
          // Ensure notifications object exists
          if (!this.local_prefs.notifications) {
            Vue.set(this.local_prefs, 'notifications', { user_signup_from_token: false });
          }
          if (this.local_prefs.notifications.user_signup_from_token === undefined) {
            Vue.set(this.local_prefs.notifications, 'user_signup_from_token', false);
          }
          if (this.local_prefs.custom_help_text === undefined) {
            Vue.set(this.local_prefs, 'custom_help_text', null);
          }
          if (!this.local_prefs.default_anaglyph_orientation) {
            Vue.set(
              this.local_prefs,
              'default_anaglyph_orientation',
              (this.app_config && this.app_config.default_anaglyph_orientation) || 'cyan-red',
            );
          }
          if (this.local_prefs.disallow_different_glasses === undefined) {
            Vue.set(this.local_prefs, 'disallow_different_glasses', false);
          }
        } else {
          messageBus.$emit('api-error', resp);
        }
      });
    },
    close_prefs_edit() {
      this.$emit('close');
    },
    is_custom_help_text_empty(html) {
      if (!html || typeof html !== 'string') return true;
      const stripped = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
      return stripped === '';
    },
    submit() {
      this.in_flight = true;
      const payload = { ...this.local_prefs };
      if (this.is_custom_help_text_empty(payload.custom_help_text)) {
        payload.custom_help_text = null;
      }
      axios
        .put('/api/clinic/' + this.clinic.id + '/prefs', payload)
        .then((response) => {
          this.in_flight = false;
          let resp = response.data;
          if (resp.status == 'error') {
            messageBus.$emit('api-error', resp);
          } else {
            messageBus.$emit('success', this.$t('clinic_prefs_edit.prefs_updated'));
            
            // Reload clinic preferences and update Vuex store
            this.reloadClinicPrefs();
            
            this.$emit('clinic-prefs-updated', this.local_prefs);
            this.$emit('close');
          }
        });
    },
    reloadClinicPrefs() {
      // Reload clinic preferences for the current clinic
      axios.get('/api/clinic/' + this.clinic.id + '/prefs').then((response) => {
        let resp = response.data;
        if (resp.status == 'success') {
          const updatedPrefs = ClinicPrefs.fromJson(resp.data);
          
          // Only update current clinic prefs if this clinic is the current clinic
          if (this.clinic.id === this.current_clinic_id) {
            this.$store.commit('setCurrentClinicPrefs', updatedPrefs);
          }
          
          // Also update the clinic_prefs array in the store
          const currentClinicPrefs = this.$store.state.clinic_prefs;
          const updatedClinicPrefs = currentClinicPrefs.map(pref => 
            pref.clinic_id === this.clinic.id ? updatedPrefs : pref
          );
          this.$store.commit('setClinicPreferences', updatedClinicPrefs);
        } else {
          messageBus.$emit('api-error', resp);
        }
      });
    },
    on_logo_selected(file) {
      if (!file) {
        return;
      }

      this.logo_preview_url = URL.createObjectURL(file);

      // Extract file extension from original filename
      const file_extension = file.name.split('.').pop();
      const logo_filename = `logo.${file_extension}`;

      const form = new FormData();
      form.append('file', file);
      form.append('clinic_id', this.clinic.id);
      form.append('title', file.name);
      form.append('description', '');
      form.append('filename', logo_filename);
      form.append('mime', file.type);
      form.append('public', 'true');
      form.append('category', 'clinic_whitelabel_logo');

      axios
        .post('/api/file/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          let resp = response.data;
          if (resp.status == 'success') {
            const new_id = resp.data;
            this.local_prefs.whitelabel.logo = new_id;
            this.fetch_logo_url(new_id, true);
          } else {
            messageBus.$emit('api-error', resp);
          }
        });
    },
    fetch_logo_url(logo_id, is_preview) {
      axios.get('/api/file/' + logo_id).then((response) => {
        let resp = response.data;
        if (resp.status == 'success') {
          const file = resp.data;
          const url = '/api/file/' + logo_id + '/download/' + file.filename;
          if (is_preview) {
            this.logo_preview_url = url;
            this.existing_logo_url = null;
          } else {
            this.existing_logo_url = url;
          }
        } else {
          messageBus.$emit('api-error', resp);
        }
      });
    },
    remove_logo() {
      // Clear the logo from local preferences
      this.local_prefs.whitelabel.logo = null;
      this.logo_preview_url = null;
      this.existing_logo_url = null;
      this.logo_file = null;
      this.show_remove_button = false;
    },
  },
};
</script>

<style>
#clinic_prefs_edit_modal .v-messages__message {
  line-height: normal;
}

.logo-preview-container {
  position: relative;
  display: inline-block;
}

.remove-logo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.remove-logo-btn {
  z-index: 1;
}

#clinic_prefs_edit_modal .ql-editor {
  min-height: 180px;
}
</style>

